/**
 * Logger utility with configurable log levels
 * 
 * Log levels (from most to least verbose):
 *   - debug: Most verbose, shows everything (debug, log, info, warn, error)
 *   - log: Standard development logging (log, info, warn, error)
 *   - info: Important info only (info, warn, error)
 *   - warn: Warnings and errors only (warn, error)
 *   - error: Errors only (error)
 *   - none: No logging (warn/error still show in production)
 * 
 * Configuration:
 *   - NEXT_PUBLIC_LOG_LEVEL: Set to "debug", "log", "info", "warn", "error", or "none"
 *   - NEXT_PUBLIC_APP_ENV: 
 *       - "debug" = debug level (everything)
 *       - "development" = log level (standard dev logging)
 *       - "staging" = log level
 *       - "production" = error/warn only
 *   - NEXT_PUBLIC_ENABLE_LOGGING: If "true", enables log level (backward compatibility)
 * 
 * Usage:
 *   import { log, debug, info, warn, error } from '@/utils/logger';
 *   
 *   log('User logged in', { userId: '123' });
 *   debug('Debug info', data);
 *   error('Error occurred', error);
 */

type LogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error' | 'none';

// Log level hierarchy (higher number = more verbose)
const LOG_LEVELS: Record<LogLevel, number> = {
  none: 0,
  error: 1,
  warn: 2,
  info: 3,
  log: 4,
  debug: 5,
};

/**
 * Get the current log level from environment variables
 */
const getLogLevel = (): LogLevel => {
  if (typeof process !== 'undefined' && process.env) {
    // Explicit log level setting (HIGHEST priority - overrides everything)
    const explicitLevel = process.env.NEXT_PUBLIC_LOG_LEVEL as LogLevel;
    if (explicitLevel && LOG_LEVELS[explicitLevel] !== undefined) {
      return explicitLevel;
    }
    
    // Use NEXT_PUBLIC_APP_ENV to determine log level (second priority)
    const appEnv = process.env.NEXT_PUBLIC_APP_ENV;
    if (appEnv === 'debug') {
      return 'debug';
    }
    if (appEnv === 'development' || appEnv === 'staging') {
      return 'log';
    }
    if (appEnv === 'production') {
      return 'error';
    }
    
    // Backward compatibility: NEXT_PUBLIC_ENABLE_LOGGING
    if (process.env.NEXT_PUBLIC_ENABLE_LOGGING === 'true') {
      return 'log';
    }
    
    // Check VERCEL_ENV (only if no explicit level set)
    if (process.env.VERCEL_ENV === 'development' || process.env.VERCEL_ENV === 'preview') {
      return 'log';
    }
    
    // Check NODE_ENV (only if no explicit level set)
    if (process.env.NODE_ENV === 'development') {
      return 'log';
    }
    
    // Check NEXT_PHASE (only if no explicit level set)
    if (process.env.NEXT_PHASE === 'development' || process.env.NEXT_PHASE === 'phase-development-server') {
      return 'log';
    }
  }
  
  // Client-side: __DEV__ check (only if no explicit level set)
  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    return 'log';
  }
  
  // Default: error level (only errors/warnings in production)
  return 'error';
};

// Cache the log level (recalculate on each call for server-side, but cache for client-side)
let cachedLogLevel: LogLevel | null = null;
const getCurrentLogLevel = (): LogLevel => {
  // For client-side, cache the level (it's set at build time)
  if (typeof window !== 'undefined') {
    if (cachedLogLevel === null) {
      cachedLogLevel = getLogLevel();
    }
    return cachedLogLevel;
  }
  // For server-side, always check at runtime
  return getLogLevel();
};

/**
 * Check if a log level should be displayed
 */
const shouldLog = (level: LogLevel): boolean => {
  const currentLevel = getCurrentLogLevel();
  return LOG_LEVELS[level] <= LOG_LEVELS[currentLevel];
};

/**
 * Debug logging (most verbose - only shows when log level is "debug")
 */
export const debug = (...args: unknown[]): void => {
  if (shouldLog('debug')) {
    console.log(...args);
  }
};

/**
 * General logging (shows when log level is "log" or "debug")
 */
export const log = (...args: unknown[]): void => {
  if (shouldLog('log')) {
    console.log(...args);
  }
};

// Debug helper: Log the current log level on first import (only in dev)
if (typeof window !== 'undefined' && typeof __DEV__ !== 'undefined' && __DEV__) {
  const currentLevel = getCurrentLogLevel();
  console.log(`[Logger] Current log level: ${currentLevel} (NEXT_PUBLIC_LOG_LEVEL=${typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_LOG_LEVEL || 'not set' : 'N/A'})`);
}

/**
 * Info logging (shows when log level is "info", "log", or "debug")
 */
export const info = (...args: unknown[]): void => {
  if (shouldLog('info')) {
    console.info(...args);
  }
};

/**
 * Warning logging (always logs, even in production, unless level is "none")
 */
export const warn = (...args: unknown[]): void => {
  if (shouldLog('warn')) {
    console.warn(...args);
  }
};

/**
 * Error logging (always logs, even in production, unless level is "none")
 */
export const error = (...args: unknown[]): void => {
  if (shouldLog('error')) {
    console.error(...args);
  }
};

